import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import './Footer.css';

/**
 * The one place on the site that names a human, from any state.
 *
 * This mounts outside the router, so it is the only surface reaching the
 * landing page, the login page and the three refusals that end a session
 * there. The reuse guidelines it links to are public for the same reason,
 * and a member who wants to report a listing has no channel but this address.
 *
 * The address comes from the public sign-in config, so it is absent for the
 * first round trip and stays absent if that read fails. Nothing is rendered
 * then: a mailto with an empty address is a worse answer than none.
 */
function Footer() {
    const { supportEmail } = useAuth();
    const currentYear = new Date().getFullYear();

    return (
        <footer className="afe-footer text-light py-4">
            <Container>
                <Row>
                    <Col md={4} className="mb-3 mb-md-0">
                        {/*
                          Heading LEVEL is semantic, the h5/h6 classes carry the
                          size. Page content ends at h1 to h3, so an <h5>
                          element here skips a level and every page reports a
                          heading-order violation. Assistive technology builds
                          its page outline from that order, so keep these at
                          h2/h3 and change only the class when the visual size
                          needs to change.
                        */}
                        <h2 className="h5">Auraria Furniture Exchange</h2>
                        <p className="text-light opacity-75 small">
                            A shared reuse exchange for faculty and staff of MSU
                            Denver, CU Denver, the Community College of Denver,
                            and the Auraria Higher Education Center on the
                            Auraria Campus.
                        </p>
                        {supportEmail && (
                            <p className="text-light opacity-75 small mb-0">
                                Questions, or a listing that should not be here?{' '}
                                <a href={`mailto:${supportEmail}`}>
                                    {supportEmail}
                                </a>
                            </p>
                        )}
                    </Col>
                    <Col md={4} className="mb-3 mb-md-0">
                        <h3 className="h6">Campus</h3>
                        <ul className="list-unstyled small">
                            <li>
                                <a
                                    href="https://www.aurariacampus.edu"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Auraria Campus
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://www.sustainableauraria.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Auraria Sustainable Campus Program
                                </a>
                            </li>
                            <li>
                                <Link to="/guidelines">Reuse guidelines</Link>
                            </li>
                        </ul>
                    </Col>
                    <Col md={4}>
                        <h3 className="h6">Built by</h3>
                        <ul className="list-unstyled small">
                            <li>
                                <a
                                    href="https://www.msudenver.edu/department-of-computer-sciences/c3-lab/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Community-Centered Computing (C3) Lab
                                </a>
                            </li>
                        </ul>
                    </Col>
                </Row>
                <hr className="my-3" />
                <Row>
                    <Col className="text-center text-light opacity-75 small">
                        <ul className="list-inline mb-2" aria-label="Policies">
                            <li className="list-inline-item">
                                <Link to="/terms">Terms of use</Link>
                            </li>
                            <li className="list-inline-item">
                                <Link to="/privacy">Privacy</Link>
                            </li>
                            <li className="list-inline-item">
                                <Link to="/accessibility">Accessibility</Link>
                            </li>
                        </ul>
                        &copy; {currentYear} Auraria Sustainable Campus Program
                        partners. Prototype.
                    </Col>
                </Row>
            </Container>
        </footer>
    );
}

export default Footer;
